import { datadogRum } from '@datadog/browser-rum';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';

import { initPendo } from '@/services/pendo';
import { LoggedUser } from '@/types/users';
import { OrganizationUserRoleSlugs } from '@/types/users';
import { useGetMyEmployeeQuery } from '@/redux/slices/employeeApiSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { withTimeout } from '@/helpers/withTimeout';
import { isMockMode } from '@/mocks/utils/helpers';

const LAUNCH_DARKLY_IDENTIFY_TIMEOUT_MS = 10000;

type SetExternalServicesProps = {
  loggedUser: LoggedUser | null;
  extraDataRequestCompleted: boolean;
};

/**
 * Set/reset external services as org/employee changes.
 * LaunchDarkly is initialized immediately when user data is available.
 * DataDog and Pendo are initialized after employee data is loaded.
 */
export const useSetExternalServices = ({
  loggedUser,
  extraDataRequestCompleted,
}: SetExternalServicesProps) => {
  const [launchDarklyReady, setLaunchDarklyReady] = useState(false);
  const [launchDarklyError, setLaunchDarklyError] = useState(false);
  const [allServicesReady, setAllServicesReady] = useState(false);

  const {
    data: myEmployee,
    isLoading: isEmployeeLoading,
    error: employeeError,
  } = useGetMyEmployeeQuery(loggedUser?.organizationId ?? skipToken);

  const ldClient = useLDClient();

  // Initialize LaunchDarkly
  const initializeLaunchDarkly = useCallback(
    async (isStale: () => boolean) => {
      if (
        !ldClient ||
        !loggedUser ||
        !loggedUser.organizationId ||
        !loggedUser.organizationName
      )
        return;

      // Mock mode: In this case, the mocked feature flags are bootstrapped in LDProvider (see App.tsx).
      // So, we can mark LaunchDarkly as ready.
      if (isMockMode) {
        setLaunchDarklyReady(true);
        return;
      }

      try {
        await withTimeout(
          ldClient.identify({
            kind: 'multi' as const,
            user: { key: loggedUser.id, email: loggedUser.email },
            organization: {
              key: loggedUser.organizationId,
              name: loggedUser.organizationName,
            },
          }),
          LAUNCH_DARKLY_IDENTIFY_TIMEOUT_MS,
          'Timed out waiting for ldClient.identify'
        );

        // A newer identify() call may have started (and possibly already
        // settled) while this one was in flight — don't let a stale result
        // override the outcome of the latest call.
        if (isStale()) return;

        console.info('Feature Flags updated based on organization/employee');
        setLaunchDarklyReady(true);
        setLaunchDarklyError(false);
      } catch (error) {
        if (isStale()) return;

        console.error(
          'Could not update Feature Flags based on organization/employee'
        );
        window.DD_RUM?.addError(error, {
          location: 'useSetExternalServices',
        });
        // A hung/blocked LaunchDarkly connection (e.g. iframe network/CSP
        // restrictions) would otherwise leave the client on its pre-identify
        // (anonymous) context/flags. Rather than silently proceeding with
        // flags that weren't evaluated for this user/org, surface an error
        // and let the caller show a failed-init state instead of the app.
        setLaunchDarklyError(true);
      }
    },
    [loggedUser, ldClient]
  );

  // Initialize DataDog
  const initializeDataDog = useCallback(() => {
    if (!loggedUser) return;

    try {
      const hasDataDogConfig =
        window._env_?.VITE_DATADOG_APPLICATION_ID &&
        window._env_?.VITE_DATADOG_CLIENT_TOKEN;

      if (!hasDataDogConfig) {
        const isLocalDev =
          !window._env_?.VITE_ENV_NAME ||
          window._env_?.VITE_ENV_NAME === 'local';

        if (isLocalDev) {
          console.info(
            'DataDog not initialized - missing environment variables (expected for local development)'
          );
        } else {
          console.error('DataDog user not set, missing environment variables');
        }
        return;
      }

      const hasEmployee = !!myEmployee && !employeeError;
      let plan = null;

      if (hasEmployee) {
        plan = loggedUser.organizationPackages?.find(
          orgPkg => orgPkg.id === loggedUser.organizationPackageId
        );
      }

      datadogRum.setUserProperty('id', loggedUser.id);
      datadogRum.setUserProperty('employee_id', loggedUser.employeeId);
      datadogRum.setUserProperty('email', loggedUser.email);
      datadogRum.setUserProperty('customer_id', loggedUser.organizationId);
      datadogRum.setUserProperty('customer_name', loggedUser.organizationName);
      datadogRum.setUserProperty('plan', plan?.package.name ?? '');
    } catch (error) {
      console.error('DataDog initialization failed:', error);
    }
  }, [loggedUser, myEmployee, employeeError]);

  // Initialize Pendo
  const initializePendo = useCallback(() => {
    if (!loggedUser) return;

    try {
      const hasEmployee = !!myEmployee && !employeeError;
      let plan = null;

      if (hasEmployee) {
        plan = loggedUser.organizationPackages?.find(
          orgPkg => orgPkg.id === loggedUser.organizationPackageId
        );
      }

      initPendo({
        visitor: {
          id: loggedUser.id ?? '',
          email: loggedUser.email,
          isAdmin:
            loggedUser.organizationUserRoles?.some(
              role => role.slug === OrganizationUserRoleSlugs.SUPER_ADMIN
            ) ?? false,
          isProdSupport:
            loggedUser.organizationUserRoles?.some(
              role => role.slug === OrganizationUserRoleSlugs.PRODUCT_SUPPORT
            ) ?? false,
        },
        account: {
          id: loggedUser.organizationId ?? '',
          name: loggedUser.organizationName ?? '',
          licenseCount: plan?.licenseCount ?? null,
          plan: plan?.package.name ?? '',
        },
      });
    } catch (error) {
      console.error('Pendo initialization failed:', error);
    }
  }, [loggedUser, myEmployee, employeeError]);

  // Initialize LaunchDarkly when user data is available
  useEffect(() => {
    if (launchDarklyReady) return;

    let cancelled = false;
    initializeLaunchDarkly(() => cancelled);

    // If loggedUser/ldClient change again before this call settles, mark it
    // stale so its result can't clobber the outcome of the newer call.
    return () => {
      cancelled = true;
    };
  }, [loggedUser, ldClient, launchDarklyReady, initializeLaunchDarkly]);

  // Initialize Datadog and Pendo when employee data is available
  useEffect(() => {
    if (allServicesReady) return;

    const isReadyToInitialize =
      launchDarklyReady &&
      loggedUser &&
      extraDataRequestCompleted &&
      !isEmployeeLoading;

    if (!isReadyToInitialize) return;

    console.info('**** External Services: started ****');
    initializeDataDog();
    initializePendo();
    console.info('**** External Services: finished ****');
    setAllServicesReady(true);
  }, [
    launchDarklyReady,
    allServicesReady,
    loggedUser,
    extraDataRequestCompleted,
    isEmployeeLoading,
    myEmployee,
    employeeError,
    initializeDataDog,
    initializePendo,
  ]);

  // Log errors if external services are not initialized because of a Launch Darkly init error or missing user data
  useEffect(() => {
    if (extraDataRequestCompleted) {
      if (!loggedUser) {
        window.DD_RUM?.addError(
          'External services not initialized yet, loggedUser is null',
          {
            location: 'useSetExternalServices',
          }
        );
      }
      if (!ldClient) {
        window.DD_RUM?.addError(
          'External services not initialized yet, ldClient is null',
          {
            location: 'useSetExternalServices',
          }
        );
      }
    }
  }, [extraDataRequestCompleted, loggedUser, ldClient]);

  return {
    externalServicesFirstSetCompleted: launchDarklyReady,
    launchDarklyError,
  };
};
